import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  CardHeader,
  Typography,
  Grid,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../axios/api";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import CustomButton from "../../componentsII/Buttons";

const AddEquipmentModal = ({ refetch, onClose }) => {
  const dispatch = useDispatch();
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [equipmentTypeQueries, setEquipmentTypeQueries] = useState({
    fuel: 0,
    gas: 0,
    refrigrant: 0,
  });
  const { user } = useSelector((state) => state);
  const year = useSelector((state) => state.user.selectedYear);

  const fetchEquipmentTypes = async ({ fuel, gas, refrigrant }) => {
    const response = await api(
      `/equipment/equipmenttype?fuel=${fuel}&gas=${gas}&refrigrant=${refrigrant}`
    );
    setEquipmentTypes(response.data.data);
  };

  useEffect(() => {
    fetchEquipmentTypes({ ...equipmentTypeQueries });
  }, [
    equipmentTypeQueries?.gas,
    equipmentTypeQueries?.fuel,
    equipmentTypeQueries?.refrigrant,
  ]);

  useEffect(() => {
    const fetchLocations = async () => {
      const response = await api(
        `/company/companylocation?company_id=${user.user.company.id}&year_id=${year.id}`
      );
      setLocations(response.data.data);
    };
    fetchLocations();
  }, []);

  const schema = yup.object().shape({
    equipment_name_code: yup
      .string()
      .required("Equipment Name/Code is required"),
    consume_fuel: yup.number().required("Fuel consumption is required"),
    consume_refrigerant: yup
      .number()
      .required("Refrigerant consumption is required"),
    consume_ind_gas: yup
      .number()
      .required("Industrial gas consumption is required"),
    equipment_type_id: yup.string().required("Equipment Type is required"),
    comp_location_id: yup.string().required("Location is required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: 0,
      company_id: user.user.company.id,
      comp_data_year_id: user?.selectedYear?.id,
      equipment_name_code: "",
      consume_fuel: 0,
      consume_refrigerant: 0,
      consume_ind_gas: 0,
      equipment_type_id: "",
      comp_location_id: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    const body = {
      id: data.id || 0,
      company_id: user.user.company.id || 0,
      comp_data_year_id: data.comp_data_year_id,
      equipment_name_code: data.equipment_name_code,
      consume_fuel: data.consume_fuel,
      consume_refrigerant: data.consume_refrigerant,
      consume_ind_gas: data.consume_ind_gas,
      equipment_type_id: data.equipment_type_id,
      comp_location_id: data.comp_location_id,
    };
    try {
      const response = await api.post(
        "/equipment/addUpdateCompanyEquipment",
        body
      );
      if (response.data.status === "200") {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
        refetch();
        onClose();
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        onClose();
      }
    } catch (error) {
      onClose();
      console.error("Error submitting form:", error);
    }
  };

  const handleReset = () => {
    reset();
  };

  const handleConsumeFuel = (bool) => {
    fetchEquipmentTypes();
  };

  return (
    <Box sx={{ width: 650, margin: "auto" }}>
      <CardHeader title={<Typography variant="h5">Add Equipment</Typography>} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="equipment_name_code"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={
                <span>
                  Equipment Name/Code
                  <span style={{ color: 'red' }}> *</span>
                </span>
              }
              fullWidth
              error={!!errors.equipment_name_code}
              helperText={errors.equipment_name_code?.message}
              margin="normal"
            />
          )}
        />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel>Consume Fuel<span style={{ color: 'red' }}> *</span></FormLabel>
              <Controller
                name="consume_fuel"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setEquipmentTypeQueries((prev) => ({
                        ...prev,
                        fuel: e.target.value,
                      }));
                    }}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel>Consume Refrigerant<span style={{ color: 'red' }}> *</span></FormLabel>
              <Controller
                name="consume_refrigerant"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setEquipmentTypeQueries((prev) => ({
                        ...prev,
                        refrigrant: e.target.value,
                      }));
                    }}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel>Consume Industrial Gas<span style={{ color: 'red' }}> *</span></FormLabel>
              <Controller
                name="consume_ind_gas"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setEquipmentTypeQueries((prev) => ({
                        ...prev,
                        gas: e.target.value,
                      }));
                    }}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="equipment_type_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.equipment_type_id}
                  >
                    <MenuItem value="" disabled>
                      Select Equipment Type<span style={{ color: 'red' }}> *</span>
                    </MenuItem>
                    {equipmentTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.equipment_type_id && (
                <p style={{ color: "red", fontSize: "0.75rem" }}>
                  {errors.equipment_type_id.message}
                </p>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="comp_location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.comp_location_id}
                  >
                    <MenuItem value="" disabled>
                      Select Location<span style={{ color: 'red' }}> *</span>
                    </MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.loc_name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.comp_location_id && (
                <p style={{ color: "red", fontSize: "0.75rem" }}>
                  {errors.comp_location_id.message}
                </p>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid item xs={12} marginTop={2}>
          <div className="flex gap-4 justify-end">
            <CustomButton
              type="button"
              className="bg-gray-500 hover:bg-gray-700 text-white"
              click={handleReset}
              text="Reset"
            />
            <CustomButton
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white"
              text="Submit"
            />
          </div>
        </Grid>
      </form>
    </Box>
  );
};

export default AddEquipmentModal;
